import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchVehicleFairValueDashboard,
  fetchVehicleValuationDiagnostics,
  recalculateAllFairValue,
  recalculateListingFairValue,
} from "../../../../services/vehicleFairValueApi";
import { vehicleMarketDetailPath } from "../../../../constants/transport";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import { formatCop } from "../../mobility/vehicleMarketFormat";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const VehicleFairValuePage = ({ onClose }) => {
  const [overview, setOverview] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [toast, setToast] = useState("");
  const [labelFilter, setLabelFilter] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [data, diag] = await Promise.all([
        fetchVehicleFairValueDashboard({
          fairValueLabel: labelFilter || undefined,
          limit: 200,
        }),
        fetchVehicleValuationDiagnostics().catch(() => null),
      ]);
      setOverview(data.overview || null);
      setDiagnostics(diag?.diagnostics || null);
      setListings(data.listings || []);
    } catch (err) {
      setErrorMessage(err.message || "Unable to load fair value dashboard.");
    } finally {
      setLoading(false);
    }
  }, [labelFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRecalcAll = async () => {
    try {
      const result = await recalculateAllFairValue({ limit: 300 });
      setToast(`Recalculated ${result.result?.processed ?? 0} listings.`);
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  const handleRecalcRow = async (listingId) => {
    try {
      await recalculateListingFairValue(listingId);
      setToast("Estimate recalculated.");
      load();
    } catch (err) {
      setToast(err.message || "Recalculation failed.");
    }
  };

  return (
    <div className="admin-dashboard admin-vehicle-fair-value-dash admin-vehicle-sources-dash--full-width">
      <header className="admin-dashboard__header">
        <div>
          <h1>Vehicle Fair Value</h1>
          <p className="muted">Fair-value v2 — comps, Fasecolda reference, hybrid blend, confidence-gated labels.</p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button type="button" className="crm-btn crm-btn-secondary" onClick={handleRecalcAll}>
            Recalculate batch
          </button>
          {onClose ? (
            <button type="button" className="crm-btn crm-btn-secondary" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {toast ? <p className="admin-toast">{toast}</p> : null}
      {errorMessage ? <AdminStatePanel variant="error" message={errorMessage} /> : null}

      {diagnostics?.summary ? (
        <div className="admin-summary-cards admin-summary-cards--secondary">
          <div className="admin-summary-card">
            <span>Valuation coverage</span>
            <strong>{diagnostics.summary.valuationCoveragePercent}%</strong>
          </div>
          <div className="admin-summary-card">
            <span>Fasecolda match</span>
            <strong>{diagnostics.summary.fasecoldaMatchRatePercent}%</strong>
          </div>
          <div className="admin-summary-card">
            <span>Comp coverage</span>
            <strong>{diagnostics.summary.marketCompCoveragePercent}%</strong>
          </div>
          <div className="admin-summary-card">
            <span>Large deviations</span>
            <strong>{diagnostics.summary.largePriceDeviationCount}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Last refresh</span>
            <strong>{formatDate(diagnostics.summary.lastValuationRefresh)}</strong>
          </div>
        </div>
      ) : null}

      {diagnostics?.methodDistribution ? (
        <details className="admin-diagnostics-panel">
          <summary>Valuation diagnostics (M22)</summary>
          <div className="admin-diagnostics-grid">
            <div>
              <h3>Methods</h3>
              <ul>
                {Object.entries(diagnostics.methodDistribution).map(([method, count]) => (
                  <li key={method}>
                    {method}: {count}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Confidence</h3>
              <ul>
                {Object.entries(diagnostics.confidenceDistribution || {}).map(([label, count]) => (
                  <li key={label}>
                    {label}: {count}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Unavailable reasons</h3>
              <ul>
                {Object.entries(diagnostics.unavailableReasons || {}).map(([reason, count]) => (
                  <li key={reason}>
                    {reason}: {count}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </details>
      ) : null}

      {overview ? (
        <div className="admin-summary-cards">
          <div className="admin-summary-card">
            <span>With estimate</span>
            <strong>{overview.listingsWithFairValue}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Great deals</span>
            <strong>{overview.greatDeals}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Good deals</span>
            <strong>{overview.goodDeals}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Fair</span>
            <strong>{overview.fairListings}</strong>
          </div>
          <div className="admin-summary-card">
            <span>High</span>
            <strong>{overview.highListings}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Overpriced</span>
            <strong>{overview.overpriced}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Insufficient data</span>
            <strong>{overview.insufficientData}</strong>
          </div>
          <div className="admin-summary-card">
            <span>Avg confidence</span>
            <strong>{overview.averageConfidence ?? "—"}</strong>
          </div>
        </div>
      ) : null}

      <div className="admin-filters-row">
        <label>
          Label
          <select value={labelFilter} onChange={(e) => setLabelFilter(e.target.value)}>
            <option value="">All</option>
            <option value="GREAT_DEAL">Great Deal</option>
            <option value="GOOD_DEAL">Good Deal</option>
            <option value="FAIR">Fair</option>
            <option value="HIGH">High</option>
            <option value="OVERPRICED">Overpriced</option>
            <option value="INSUFFICIENT_DATA">Insufficient</option>
          </select>
        </label>
      </div>

      {loading ? (
        <AdminStatePanel message="Loading fair value estimates…" />
      ) : (
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Vehicle</th>
                <th>Source</th>
                <th>Current</th>
                <th>Est. fair</th>
                <th>Delta</th>
                <th>Label</th>
                <th>Confidence</th>
                <th>Comps</th>
                <th>Level</th>
                <th>Calculated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {listings.map((row) => (
                <tr key={row.vehicleListingId}>
                  <td>{row.title}</td>
                  <td>{row.sourceName}</td>
                  <td>{formatCop(row.currentPriceCop)}</td>
                  <td>{formatCop(row.estimatedFairPriceCop)}</td>
                  <td>
                    {row.priceVsFairValuePct != null ? `${row.priceVsFairValuePct}%` : "—"}
                  </td>
                  <td>{row.fairValueLabel || "—"}</td>
                  <td>{row.confidenceLabel || "—"}</td>
                  <td>{row.comparableCount ?? "—"}</td>
                  <td>{row.comparisonLevel || "—"}</td>
                  <td>{formatDate(row.calculatedAt)}</td>
                  <td>
                    <Link to={vehicleMarketDetailPath(row.vehicleListingId)}>Detail</Link>
                    {" · "}
                    <button
                      type="button"
                      className="admin-link-btn"
                      onClick={() => handleRecalcRow(row.vehicleListingId)}
                    >
                      Recalc
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      )}
    </div>
  );
};

export default VehicleFairValuePage;
